import { FC, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { Provider as ReduxProvider } from 'react-redux';
import { createBrowserRouter, Outlet, RouterProvider } from 'react-router-dom';
import { InteractionType } from '@azure/msal-browser';
import { AuthenticatedTemplate, useMsalAuthentication } from '@azure/msal-react';
import { CacheProvider } from '@emotion/react';
import { CssBaseline, GlobalStyles, PaletteMode, Theme } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import { scopes } from '@api/auth/msalConfig';
import UserProvider from '@api/auth/useUser/provider';
import { HasPermission, useHasPermission } from '@components/auth';
import { AppLayout } from '@components/base/layout/AppLayout';
import { ErrorBoundary } from '@components/feedback/ErrorBoundary';
import { NoAccess } from '@components/feedback/NoAccess';
import env, { isLocal } from '@config/runtimeConfig';
import { useDocumentTitleValue } from '@hooks/useDocumentContext';
import GlobalViewModeProvider from '@hooks/useGlobalViewMode/provider';
import { useLocalStorage } from '@hooks/useLocalStorage';
import MetadataContextProvider from '@hooks/useMetadataContext/provider';
import useSearchContext, { SearchContext } from '@hooks/useSearchContext';
import StyledSnackbarProvider from '@hooks/useSnack/provider';
import SourceIconProvider from '@hooks/useSourceIcon/provider';
import SubscriberProvider from '@hooks/useSubscriber/provider';
import { TitlesDataSourceProvider } from '@hooks/useTitlesDataSource';
import { ToggleThemeProvider } from '@hooks/useToggleTheme/provider';
import { MasterIdManagerContextProvider } from '@modules/masterIdManager/useMasterIdManager/provider';
import i18n from 'src/i18n';
import { darkTheme, lightTheme } from 'src/theme';

import { getAppRoutes } from './appRoutes';
import emotionCache from './emotionCache';
import { store } from './store';

//if (import.meta.env.VITE__ENV === 'dev' || import.meta.env.VITE__ENV === 'at') await worker.start();

const WithRoutes: FC = () => {
  const canWrite = useHasPermission();

  const router = createBrowserRouter([
    {
      children: getAppRoutes(canWrite),
      element: (
        <HasPermission action='read' fallback={<NoAccess />}>
          <StyledSnackbarProvider>
            <TitlesDataSourceProvider>
              <SubscriberProvider>
                <MetadataContextProvider>
                  <GlobalViewModeProvider>
                    <MasterIdManagerContextProvider>
                      <AppLayout>
                        <Outlet />
                      </AppLayout>
                    </MasterIdManagerContextProvider>
                  </GlobalViewModeProvider>
                </MetadataContextProvider>
              </SubscriberProvider>
            </TitlesDataSourceProvider>
          </StyledSnackbarProvider>
        </HasPermission>
      ),
      errorElement: <ErrorBoundary />,
      path: '/',
    },
  ]);

  return <RouterProvider router={router} />;
};

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (query.state.data !== undefined) console.error(`Something went wrong: ${error.message}`);
    },
  }),
  defaultOptions: {
    queries: {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      retry: 0,
      staleTime: 1000 * 60 * 5, // 5 minutes
    },
  },
});

const DocumentTitleHelmet: FC = () => {
  const { t } = useTranslation();
  const pageTitle = useDocumentTitleValue();
  const docTitleBase = t('appName', { context: env });

  return <Helmet title={pageTitle} titleTemplate={`%s – ${docTitleBase}`} />;
};

const App: FC = () => {
  const { result: authResult } = useMsalAuthentication(InteractionType.Redirect, { scopes });

  const [theme] = useLocalStorage<PaletteMode, Theme>('theme', 'dark', mode =>
    mode === 'dark' ? darkTheme : lightTheme
  );

  const searchContext = useSearchContext();
  useEffect(() => {
    document.documentElement.lang = i18n.language;

    let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement;

    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.getElementsByTagName('head')[0].appendChild(link);
    }

    link.href = [
      '/favicon',
      ...(env !== 'prod' ? [`-${env}`] : []),
      ...(isLocal ? ['_LOCAL'] : []),
      '.ico',
    ].join('');
  }, []);

  return (
    <>
      <DocumentTitleHelmet />

      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <AuthenticatedTemplate>
          <ReduxProvider store={store}>
            <QueryClientProvider client={queryClient}>
              <SourceIconProvider>
                <CacheProvider value={emotionCache}>
                  <ThemeProvider theme={theme}>
                    <ToggleThemeProvider>
                      <GlobalStyles
                        styles={{
                          'html, body': {
                            height: '100%',
                            ...(theme.palette.mode === 'dark' ? { colorScheme: 'dark' } : {}),
                          },
                          body: { display: 'flex', flexDirection: 'column' },
                          'body > div': {
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                          },
                          "input[type='number']::-webkit-inner-spin-button": {
                            webkitAppearance: 'none',
                          },
                          'ul, ol': {
                            margin: 0,
                            paddingLeft: 20,
                          },
                          '::selection': {
                            background: theme.palette.primary[theme.palette.mode],
                            color: theme.palette.getContrastText(
                              theme.palette.primary[theme.palette.mode]
                            ),
                          },
                        }}
                      />
                      <CssBaseline />

                      <SearchContext.Provider value={searchContext}>
                        {authResult && (
                          <UserProvider result={authResult}>
                            <WithRoutes />
                          </UserProvider>
                        )}
                      </SearchContext.Provider>
                    </ToggleThemeProvider>
                  </ThemeProvider>
                </CacheProvider>
              </SourceIconProvider>

              <ReactQueryDevtools />
            </QueryClientProvider>
          </ReduxProvider>
        </AuthenticatedTemplate>
      </LocalizationProvider>
    </>
  );
};

export default App;
